/**
 * Iter-193n — Cabinet internal expenses page (CEO + GESTIONNAIRE only).
 *
 * Mirrors /expenses but for company internal spending (rent, software,
 * supplier invoices…). No reimbursement workflow — only DRAFT → VALIDATED.
 * Reuses the same OCR + cropper + edit dialog primitives.
 *
 * Profitability KPI formula (documented in code so the next agent picks it up):
 *
 *   Profit_net(period) =
 *      Σ revenue(invoiced missions in period)
 *    − Σ mission_costs(period)           // salaries + freelance + portage fees
 *    − Σ cabinet_expenses(period)        // VALIDATED only, from THIS page
 *    − Σ personal_NDF(period)            // PAID, non-billable
 *    − Σ personal_NDF_billable_unbilled  // PAID, billable, invoice not yet emitted
 *
 * Wired KPI lives in backend (future iter). This page focuses on data entry.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { api, API } from "../lib/api";
import ExpenseSearchBar, { applySearchFilters, searchRowsToCsv, downloadCsv } from "../components/ExpenseSearchBar";
import { SwitchLineButton } from "@/components/SwitchLineButton";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowDown, ArrowUp, Camera, Check, ChevronLeft, ChevronRight, FileText, Loader2,
  Pencil, Plus, RefreshCw, Send, Trash2, X,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const FR_MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export default function CabinetExpenses() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [note, setNote] = useState(null);
  const [lines, setLines] = useState([]);
  const [feature, setFeature] = useState({ categories: [], supported_mimes: [], accepted_file_types_label: "" });
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const ocrAbortRef = useRef(null);
  const [editLine, setEditLine] = useState(null);
  const fileInputRef = useRef(null);
  // Iter-193u — date sorting toggle for the lines table. Default: descending
  // (most recent at top).
  const [sortDir, setSortDir] = useState("desc");
  // Iter-228x-bis — cross-month search/filter. When `searchValue.preset === "month"`
  // we use the existing per-month load. Otherwise we hit /expenses/search?kind=cabinet.
  const [searchValue, setSearchValue] = useState(null);
  const [searchLines, setSearchLines] = useState(null);  // null = no active search
  const [switchCap, setSwitchCap] = useState(null); // Iter-314 — CEO NDF ⇄ cabinet switch

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, f] = await Promise.all([
        api.get(`/cabinet-expenses/notes/by-month/${year}/${month}`),
        api.get("/cabinet-expenses/feature-status"),
      ]);
      api.get(`/expenses/switch/status?year=${year}&month=${month}`).then((r) => setSwitchCap(r.data)).catch(() => setSwitchCap(null));
      setNote(n.data.note);
      setLines(n.data.lines || []);
      setFeature(f.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  // Iter-228x-bis — when the search preset is broader than 'month', fetch
  // cross-month rows from the unified search endpoint.
  useEffect(() => {
    if (!searchValue || searchValue.preset === "month") { setSearchLines(null); return; }
    const params = new URLSearchParams({ kind: "cabinet" });
    if (searchValue.from) params.append("date_from", searchValue.from);
    if (searchValue.to) params.append("date_to", searchValue.to);
    if (searchValue.vendor) params.append("vendor", searchValue.vendor);
    (searchValue.category || []).forEach((c) => params.append("category", c));
    api.get(`/expenses/search?${params.toString()}`)
      .then((r) => setSearchLines(r.data?.lines || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Échec recherche"));
  }, [searchValue]);

  // Iter-228x-bis — Compute the displayed lines + total ONCE so the header
  // and the table footer both reflect what's currently shown (and not the
  // raw monthly note totals).
  const visibleLines = useMemo(
    () => searchLines ?? applySearchFilters(lines, searchValue || {}),
    [searchLines, lines, searchValue],
  );
  const visibleTotalMad = useMemo(
    () => visibleLines.reduce((acc, l) => acc + (Number(l.amount_mad) || 0), 0),
    [visibleLines],
  );
  const visibleTvaRecup = useMemo(
    () => visibleLines.reduce((acc, l) => {
      if ((l.currency || "").toUpperCase() !== "MAD") return acc;
      return acc + (Number(l.vat_amount) || 0);
    }, 0),
    [visibleLines],
  );
  // Iter-228x-bis — Totaux HT/TTC (MAD uniquement — la TVA récupérable
  // ne s'applique qu'aux factures marocaines). Utile pour préparer la
  // déclaration TVA par catégorie.
  const visibleTotalHt = useMemo(
    () => visibleLines.reduce((acc, l) => {
      if ((l.currency || "").toUpperCase() !== "MAD") return acc;
      return acc + (Number(l.amount_ht) || 0);
    }, 0),
    [visibleLines],
  );
  const visibleTotalTtc = useMemo(
    () => visibleLines.reduce((acc, l) => {
      if ((l.currency || "").toUpperCase() !== "MAD") return acc;
      return acc + (Number(l.amount_ttc) || Number(l.amount) || 0);
    }, 0),
    [visibleLines],
  );
  const isFilteredView = !!(searchValue && (
    (searchValue.category && searchValue.category.length > 0)
    || (searchValue.vendor && searchValue.vendor.trim().length > 0)
    || (searchValue.preset && searchValue.preset !== "month")
  ));

  const editable = note?.status === "DRAFT";

  const goPrev = () => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); };
  const goNext = () => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); };

  const addManual = () => {
    setEditLine({
      invoice_date: new Date().toISOString().slice(0, 10),
      vendor: "", invoice_number: "", category: "Autre", amount: "",
      currency: "MAD", description: "", _isNew: true, _queue: [],
    });
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileSelected = async (e) => {
    const fileList = Array.from(e.target.files || []);
    e.target.value = "";
    if (fileList.length === 0) return;
    const isAcceptable = (f) => {
      const t = (f.type || "").toLowerCase();
      if (feature.supported_mimes.includes(t)) return true;
      return /\.(jpg|jpeg|png|webp|heic|heif|pdf)$/i.test(f.name || "");
    };
    const unsupported = fileList.filter((f) => !isAcceptable(f));
    if (unsupported.length) {
      toast.error(`Format non supporté : ${unsupported.map((f) => f.name).join(", ")}.`);
      return;
    }
    // Iter-194a — bulk scan cap (matches backend /expenses/ocr/bulk limit).
    if (fileList.length > 10) {
      toast.error("Maximum 10 factures à la fois. Réessayez avec un lot plus petit.");
      return;
    }
    await runOcrOnReadyFiles(fileList);
  };

  // Iter-193o — Auto-crop server-side then OCR. Bulk runs in parallel.
  const runOcrOnReadyFiles = async (files) => {
    if (!files || files.length === 0) return;
    const controller = new AbortController();
    ocrAbortRef.current = controller;
    setScanning(true);
    try {
      if (files.length === 1) {
        const fd = new FormData(); fd.append("file", files[0]);
        const r = await api.post("/expenses/ocr", fd, {
          headers: { "Content-Type": "multipart/form-data" }, signal: controller.signal,
        });
        const d = r.data.draft || {};
        setEditLine({
          invoice_date: d.invoice_date || new Date().toISOString().slice(0, 10),
          vendor: d.vendor || "", invoice_number: d.invoice_number || "",
          category: d.category || "Autre", amount: d.amount || "",
          currency: d.currency || "MAD", description: d.description || "",
          // Iter-207bb — VAT fields from OCR (MAD invoices).
          amount_ht: d.amount_ht ?? "",
          vat_amount: d.vat_amount ?? "",
          vat_rate: d.vat_rate ?? "",
          amount_ttc: d.amount_ttc ?? (d.amount ?? ""),
          confidence: d.confidence,
          staged_file_id: r.data.staged_file?.staged_file_id,
          _isNew: true, _queue: r.data.extra_items || [],
          _queueTotal: 1 + (r.data.extra_items || []).length,
          _queueIndex: 1,
        });
        if ((r.data.extra_items || []).length) {
          toast.success(`${1 + r.data.extra_items.length} factures détectées dans ce fichier — elles vont s'enchaîner.`);
        }
      } else {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const r = await api.post("/expenses/ocr/bulk", fd, {
          headers: { "Content-Type": "multipart/form-data" }, signal: controller.signal,
        });
        const allItems = r.data.items || [];
        const items = allItems.filter((it) => !it.error && it.draft);
        const errors = allItems.filter((it) => it.error);
        errors.forEach((it) => toast.error(`${it.filename} : ${it.error}`));
        if (items.length === 0) {
          toast.error("Aucun ticket n'a pu être analysé.");
          return;
        }
        if (items.length < allItems.length) {
          toast.warning(`${items.length}/${allItems.length} tickets analysés. ${errors.length} échec(s).`);
        } else {
          toast.success(`${items.length} tickets analysés en parallèle.`);
        }
        const first = items[0];
        const queue = items.slice(1);
        const d = first.draft || {};
        setEditLine({
          invoice_date: d.invoice_date || new Date().toISOString().slice(0, 10),
          vendor: d.vendor || "", invoice_number: d.invoice_number || "",
          category: d.category || "Autre", amount: d.amount || "",
          currency: d.currency || "MAD", description: d.description || "",
          // Iter-207bb — VAT fields (MAD).
          amount_ht: d.amount_ht ?? "",
          vat_amount: d.vat_amount ?? "",
          vat_rate: d.vat_rate ?? "",
          amount_ttc: d.amount_ttc ?? (d.amount ?? ""),
          confidence: d.confidence,
          staged_file_id: first.staged_file?.staged_file_id,
          _isNew: true, _queue: queue,
          _queueTotal: items.length,
          _queueIndex: 1,
        });
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        toast.info("Analyse OCR annulée");
      } else {
        toast.error(err?.response?.data?.detail || "Échec OCR");
      }
    } finally {
      setScanning(false);
      ocrAbortRef.current = null;
    }
  };

  const cancelOcr = () => { if (ocrAbortRef.current) ocrAbortRef.current.abort(); };

  const saveLine = async (payload, lineId, currentForm) => {
    try {
      let r;
      if (lineId) {
        r = await api.patch(`/cabinet-expenses/lines/${lineId}`, payload);
        toast.success("Dépense mise à jour");
      } else {
        r = await api.post("/cabinet-expenses/lines", payload);
        toast.success("Dépense ajoutée");
      }
      // Iter-292 — surface a Drive upload failure instead of silently keeping a staged copy.
      if (r?.data?.drive_warning) toast.warning(r.data.drive_warning, { duration: 9000 });
      // Iter-194a — bulk scan: if more OCR'd tickets remain in the queue,
      // pop the next one into the dialog so the user can review/save it
      // without re-opening the file picker.
      const advanced = await advanceQueue(currentForm);
      if (advanced) return;
      setEditLine(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  // Iter-258 — move to the next scanned file in the queue (returns true if advanced).
  const advanceQueue = async (currentForm) => {
    const queue = currentForm?._queue || [];
    if (queue.length === 0) return false;
    const next = queue[0];
    const rest = queue.slice(1);
    const d = next.draft || {};
    const total = currentForm._queueTotal || (queue.length + (currentForm._queueIndex || 1));
    const newIndex = (currentForm._queueIndex || 1) + 1;
    await load();
    setEditLine({
      invoice_date: d.invoice_date || new Date().toISOString().slice(0, 10),
      vendor: d.vendor || "", invoice_number: d.invoice_number || "",
      category: d.category || "Autre", amount: d.amount || "",
      currency: d.currency || "MAD", description: d.description || "",
      // Iter-207bb — VAT fields (MAD).
      amount_ht: d.amount_ht ?? "",
      vat_amount: d.vat_amount ?? "",
      vat_rate: d.vat_rate ?? "",
      amount_ttc: d.amount_ttc ?? (d.amount ?? ""),
      confidence: d.confidence,
      staged_file_id: next.staged_file?.staged_file_id,
      _isNew: true,
      _queue: rest,
      _queueTotal: total,
      _queueIndex: newIndex,
    });
    return true;
  };

  // Iter-258 — skip the current scanned expense (e.g. duplicate / already
  // booked) and continue with the next file instead of cancelling everything.
  const skipLine = async (currentForm) => {
    toast.info("Facture ignorée — passage à la suivante");
    const advanced = await advanceQueue(currentForm);
    if (!advanced) {
      setEditLine(null);
      await load();
    }
  };

  const deleteLine = async (l) => {
    if (!confirm(`Supprimer la dépense ${l.vendor} ${fmt(l.amount)} ${l.currency} ?`)) return;
    try {
      await api.delete(`/cabinet-expenses/lines/${l.cabinet_line_id}`);
      toast.success("Supprimée");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const validateNote = async () => {
    if (!confirm(`Valider la note interne de ${month}/${year} ? Plus aucune modification ne sera possible (sauf réouverture CEO).`)) return;
    try {
      await api.patch(`/cabinet-expenses/notes/${note.cabinet_note_id}/validate`);
      toast.success("Note interne validée");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const reopen = async () => {
    if (!confirm("Réouvrir la note (passage VALIDATED → DRAFT) ?")) return;
    try {
      await api.patch(`/cabinet-expenses/notes/${note.cabinet_note_id}/reopen`);
      toast.success("Note réouverte");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-zinc-500"><Loader2 className="animate-spin" size={16} /> Chargement…</div>;

  const dupCount = lines.filter((l) => l.is_duplicate).length;

  return (
    <div className="space-y-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full" data-testid="cabinet-expenses-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dépenses internes du cabinet</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Loyer, licences logicielles, fournitures, honoraires… Ces dépenses sont
          puisées directement de la trésorerie du cabinet et impactent la rentabilité globale.
        </p>
      </div>

      {/* Iter-193q — Cabinet profitability KPI moved to /admin/profitability
          (see ProfitabilityPortfolio.jsx) per user request. */}

      {/* Month navigator */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} data-testid="cab-month-prev" className="p-1.5 border border-zinc-200 rounded hover:bg-zinc-50"><ChevronLeft size={16} /></button>
          <div className="flex items-center gap-1.5" data-testid="cab-month-label">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              data-testid="cab-month-select"
              className="h-9 px-2 text-lg font-semibold bg-transparent border border-zinc-200 rounded cursor-pointer hover:bg-zinc-50"
            >
              {FR_MONTHS.map((l, idx) => <option key={idx + 1} value={idx + 1}>{l}</option>)}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              data-testid="cab-year-select"
              className="h-9 px-2 text-lg font-semibold bg-transparent border border-zinc-200 rounded cursor-pointer hover:bg-zinc-50"
            >
              {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button onClick={goNext} data-testid="cab-month-next" className="p-1.5 border border-zinc-200 rounded hover:bg-zinc-50"><ChevronRight size={16} /></button>
          <button onClick={goToday} className="text-xs text-zinc-500 hover:text-zinc-900 ml-1 font-mono uppercase tracking-wider">Aujourd'hui</button>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className={`px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-wider ${
            note?.status === "VALIDATED" ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"
          }`} data-testid="cab-status">{note?.status === "VALIDATED" ? "Validée" : "Brouillon"}</span>
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-700"><b>{lines.length}</b> ligne(s)</span>
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-900 font-semibold" data-testid="cab-total-mad">
            {isFilteredView ? "Total filtré" : "Total"} <span className="font-mono">{fmt(isFilteredView ? visibleTotalMad : note?.total_mad)} MAD</span>
          </span>
          {/* Iter-228x-bis — TVA récupérable + totaux HT/TTC MAD du set visible.
              Précieux pour préparer la déclaration TVA par catégorie. */}
          {visibleTvaRecup > 0 && (
            <>
              <span className="text-zinc-500">·</span>
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-900"
                data-testid="cab-vat-recoverable"
                title="Total HT, TVA récupérable et TTC sur les factures MAD affichées — utile pour la déclaration TVA."
              >
                <span className="text-[10px] font-mono uppercase tracking-wider">HT</span>
                <span className="font-mono font-semibold" data-testid="cab-total-ht">{fmt(Math.round(visibleTotalHt * 100) / 100)}</span>
                <span className="text-zinc-400">·</span>
                <span className="text-[10px] font-mono uppercase tracking-wider">TVA</span>
                <span className="font-mono font-semibold" data-testid="cab-total-vat">{fmt(Math.round(visibleTvaRecup * 100) / 100)}</span>
                <span className="text-zinc-400">·</span>
                <span className="text-[10px] font-mono uppercase tracking-wider">TTC</span>
                <span className="font-mono font-semibold" data-testid="cab-total-ttc">{fmt(Math.round(visibleTotalTtc * 100) / 100)}</span>
                <span className="text-[10px] font-mono text-emerald-700">MAD</span>
              </span>
            </>
          )}
          <button onClick={load} className="ml-2 p-1.5 border border-zinc-200 rounded hover:bg-zinc-50 text-xs inline-flex items-center gap-1" title="Rafraîchir"><RefreshCw size={12} /> Refresh</button>
          {/* Iter-228x-bis — Monthly CSV exports. `full` dumps every line +
              all columns (Excel-ready). `tva` exports only MAD lines with VAT,
              formatted for the Moroccan monthly VAT declaration. */}
          {lines.length > 0 && (
            <>
              <a
                href={`${API}/cabinet-expenses/notes/by-month/${year}/${month}/export?kind=full`}
                data-testid="cab-export-full"
                className="p-1.5 border border-zinc-200 rounded hover:bg-zinc-50 text-xs inline-flex items-center gap-1"
                title="Export CSV — toutes les lignes du mois (Excel/Sheets)"
              >
                <FileText size={12} /> Export CSV
              </a>
              <a
                href={`${API}/cabinet-expenses/notes/by-month/${year}/${month}/export?kind=tva`}
                data-testid="cab-export-tva"
                className="p-1.5 border border-emerald-200 bg-emerald-50 text-emerald-900 rounded hover:bg-emerald-100 text-xs inline-flex items-center gap-1"
                title="Export TVA — lignes MAD avec TVA, prêtes pour la déclaration mensuelle"
              >
                <FileText size={12} /> Déclaration TVA
              </a>
            </>
          )}
        </div>
      </div>

      {/* Capture CTA */}
      {editable && (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onPickFile}
              disabled={scanning}
              data-testid="cab-scan-btn"
              className="flex items-center justify-center gap-2 h-24 sm:h-20 rounded-lg border-2 border-dashed border-zinc-300 hover:border-zinc-950 hover:bg-zinc-50 text-zinc-700 font-medium disabled:opacity-50"
            >
              {scanning ? (<><Loader2 size={18} className="animate-spin" /> Analyse en cours…</>)
                : (<><Camera size={20} /> Scanner facture <span className="text-[10px] font-mono text-zinc-500">(jusqu'à 10 — OCR Gemini)</span></>)}
            </button>
            <button
              type="button"
              onClick={addManual}
              data-testid="cab-add-manual-btn"
              className="flex items-center justify-center gap-2 h-24 sm:h-20 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 font-medium"
            >
              <Plus size={18} /> Saisie manuelle
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
              multiple
              onChange={onFileSelected}
              className="hidden"
              data-testid="cab-file-input"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-zinc-500">
            <div>
              <span className="font-mono uppercase tracking-wider mr-2">Formats :</span>
              {feature.accepted_file_types_label}
            </div>
            {scanning && (
              <button onClick={cancelOcr} data-testid="cab-cancel-ocr"
                className="inline-flex items-center gap-1 px-2 h-7 border border-red-200 text-red-700 text-[11px] rounded hover:bg-red-50">
                Annuler l'analyse
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          {/* Iter-228x-bis — Cross-month search bar. The filter applies to
              `lines` (per-month) when preset === "month", otherwise it pulls
              fresh data from /expenses/search?kind=cabinet. */}
          <div className="px-3 pt-3">
            <ExpenseSearchBar
              testidPrefix="cab-search"
              categories={feature?.categories || []}
              initialPreset="month"
              value={searchValue}
              onChange={setSearchValue}
              count={visibleLines.length}
              onExport={() => {
                downloadCsv(`cabinet_recherche_${year}-${String(month).padStart(2, "0")}.csv`, searchRowsToCsv(visibleLines, "cabinet"));
              }}
            />
          </div>
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-zinc-50">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                <th className="px-4 py-2">
                  <button
                    type="button"
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    data-testid="cab-sort-date"
                    className="inline-flex items-center gap-1 hover:text-zinc-900"
                    title={`Trier par date (actuel: ${sortDir === "asc" ? "croissant" : "décroissant"})`}
                  >
                    Date
                    {sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                  </button>
                </th>
                <th className="px-4 py-2">Fournisseur</th>
                <th className="px-4 py-2">N° Facture</th>
                <th className="px-4 py-2">Catégorie</th>
                <th className="px-4 py-2 text-right">Montant</th>
                <th className="px-4 py-2">Devise</th>
                <th className="px-4 py-2 text-right">Conv. MAD</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleLines.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-500 text-sm">
                  {isFilteredView
                    ? "Aucune dépense ne correspond aux filtres actifs."
                    : "Aucune dépense interne saisie. Scannez ou ajoutez une ligne manuellement."}
                </td></tr>
              )}
              {[...visibleLines]
                .sort((a, b) => {
                  const da = a.invoice_date || "";
                  const db = b.invoice_date || "";
                  return sortDir === "asc" ? da.localeCompare(db) : db.localeCompare(da);
                })
                .map((l) => (
                <tr
                  key={l.cabinet_line_id}
                  className={`border-t border-zinc-100 hover:bg-zinc-50/60 ${l.is_duplicate ? "bg-red-50/60 hover:bg-red-50" : ""}`}
                  data-testid={`cab-row-${l.cabinet_line_id}`}
                  title={l.is_duplicate ? "Doublon détecté (même date/fournisseur/montant/n° facture)." : undefined}
                >
                  <td className="px-4 py-3 font-mono text-xs">
                    {l.is_duplicate && (<AlertTriangle size={12} className="inline mr-1 text-red-700" />)}
                    {l.invoice_date}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium">{l.vendor}</div>
                    {l.description && <div className="text-[10px] text-zinc-500 mt-0.5">{l.description}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-600">{l.invoice_number || "—"}</td>
                  <td className="px-4 py-3 text-xs">{l.category}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm">
                    {fmt(l.amount)}
                    {(l.currency || "").toUpperCase() === "MAD" && (l.vat_amount != null || l.amount_ht != null) && (
                      <div
                        className="text-[9px] text-zinc-500 mt-0.5"
                        data-testid={`cab-row-vat-${l.cabinet_line_id}`}
                        title={`HT ${l.amount_ht != null ? Number(l.amount_ht).toFixed(2) : "—"} · TVA ${l.vat_amount != null ? Number(l.vat_amount).toFixed(2) : "—"}${l.vat_rate != null ? ` (${Number(l.vat_rate).toFixed(0)}%)` : ""}`}
                      >
                        HT {l.amount_ht != null ? fmt(l.amount_ht) : "—"} · TVA {l.vat_amount != null ? fmt(l.vat_amount) : "—"}
                        {l.vat_rate != null && <span className="ml-0.5">({Number(l.vat_rate).toFixed(0)}%)</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-600">{l.currency}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-semibold">{fmt(l.amount_mad)}</td>
                  <td className="px-4 py-3 text-right">
                    {switchCap?.allowed && (
                      <span className="inline-flex align-middle mr-1">
                        <SwitchLineButton direction="to-ndf" lineId={l.cabinet_line_id} size="sm"
                          disabledReason={!editable ? `Bascule indisponible : la note interne de ${String(month).padStart(2, "0")}/${year} est validée — rouvrez-la d'abord` : switchCap.ndf_block}
                          onDone={load} />
                      </span>
                    )}
                    {editable && (
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setEditLine({ ...l, _isNew: false })} className="p-1 hover:bg-zinc-100 rounded" title="Modifier"><Pencil size={13} /></button>
                        <button onClick={() => deleteLine(l)} className="p-1 hover:bg-red-50 text-red-700 rounded" title="Supprimer"><Trash2 size={13} /></button>
                      </div>
                    )}
                    {l.receipt_drive_web_view_link && (
                      <a href={l.receipt_drive_web_view_link} target="_blank" rel="noreferrer" className="inline-flex items-center ml-1 p-1 hover:bg-zinc-100 rounded text-zinc-600" title="Voir le justificatif"><FileText size={13} /></a>
                    )}
                    {/* Iter-292 — receipt staged but not yet on Drive (auto-retry every 15 min). */}
                    {!l.receipt_drive_web_view_link && l.staged_file_id && !l.drive_file_id && (
                      <a
                        href={`/api/expenses/ocr/staged/${l.staged_file_id}/preview`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center ml-1 p-1 text-amber-600 hover:bg-amber-50 rounded"
                        title={`Justificatif en attente d'upload Drive — ${l.receipt_drive_error || "nouvel essai automatique toutes les 15 min"}`}
                        data-testid={`cabinet-receipt-pending-${l.cabinet_line_id}`}
                      >
                        <FileText size={13} />
                        <AlertTriangle size={9} className="-ml-1 -mt-2" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {visibleLines.length > 0 && (
              <tfoot className="bg-zinc-50 border-t border-zinc-200">
                {visibleTvaRecup > 0 && (
                  <tr className="text-[11px] text-zinc-600">
                    <td colSpan={6} className="px-4 py-1.5 text-right font-mono uppercase tracking-wider">
                      Total HT · TVA · TTC (MAD) :
                    </td>
                    <td className="px-4 py-1.5 text-right font-mono" data-testid="cab-tfoot-vat-summary">
                      {fmt(visibleTotalHt)} · {fmt(visibleTvaRecup)} · {fmt(visibleTotalTtc)}
                    </td>
                    <td />
                  </tr>
                )}
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-right text-xs uppercase tracking-wider text-zinc-500 font-mono">
                    {isFilteredView ? "Total filtré MAD" : "Total MAD"} :
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold" data-testid="cab-tfoot-total-mad">
                    {fmt(isFilteredView ? visibleTotalMad : note?.total_mad)} MAD
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Validate CTA */}
      {editable && lines.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          {dupCount > 0 ? (
            <div data-testid="cab-duplicate-banner" className="flex items-center gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <AlertTriangle size={16} />
              <span><b>{dupCount}</b> doublon(s) détecté(s). Modifiez ou supprimez avant de valider.</span>
            </div>
          ) : <div />}
          <button onClick={validateNote} disabled={dupCount > 0} data-testid="cab-validate-btn"
            className="inline-flex items-center gap-1.5 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={14} /> Valider la note interne
          </button>
        </div>
      )}
      {!editable && note?.status === "VALIDATED" && (
        <div className="flex items-center justify-end">
          <button onClick={reopen} data-testid="cab-reopen-btn" className="inline-flex items-center gap-1 px-3 h-9 border border-zinc-200 rounded text-zinc-700 hover:bg-zinc-50 text-sm">
            Réouvrir (CEO)
          </button>
        </div>
      )}

      <CabinetEditDialog
        editLine={editLine}
        categories={feature.categories}
        onClose={() => setEditLine(null)}
        onSave={saveLine}
        onSkip={skipLine}
      />
    </div>
  );
}


function CabinetEditDialog({ editLine, categories, onClose, onSave, onSkip }) {
  const [form, setForm] = useState(editLine || null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(editLine); }, [editLine]);
  if (!editLine || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const maxDate = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();

  // Iter-193r — same-origin URL so the session cookie is sent (see Expenses.jsx).
  const previewUrl = form.staged_file_id
    ? `/api/expenses/ocr/staged/${form.staged_file_id}/preview`
    : (form.receipt_drive_web_view_link || null);

  const submit = async () => {
    if (!form.invoice_date || !form.vendor || !form.amount || !form.currency) {
      toast.error("Champs requis : Date, Fournisseur, Montant, Devise");
      return;
    }
    const todayPlus1 = new Date(); todayPlus1.setDate(todayPlus1.getDate() + 1);
    if (new Date(form.invoice_date) > todayPlus1) {
      toast.error("La date du ticket ne peut pas être dans le futur.");
      return;
    }
    setSaving(true);
    const isMAD = (form.currency || "").toUpperCase() === "MAD";
    const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
    const payload = {
      invoice_date: form.invoice_date,
      vendor: form.vendor.trim(),
      invoice_number: form.invoice_number || null,
      category: form.category,
      amount: parseFloat(form.amount),
      currency: form.currency.toUpperCase(),
      description: form.description || null,
      // Iter-228x-bis — VAT breakdown is MAD-only (foreign VAT not recoverable).
      amount_ht: isMAD ? num(form.amount_ht) : null,
      vat_amount: isMAD ? num(form.vat_amount) : null,
      vat_rate: isMAD ? num(form.vat_rate) : null,
      amount_ttc: isMAD ? num(form.amount_ttc) : null,
    };
    if (form._isNew) {
      payload.staged_file_id = form.staged_file_id || null;
      payload.receipt_drive_file_id = form.receipt_drive_file_id || null;
      payload.receipt_drive_web_view_link = form.receipt_drive_web_view_link || null;
    } else if (form.staged_file_id) {
      // Iter-207j — user attached a NEW file in the edit modal → send it so the
      // backend triggers Drive upload (replaces the existing drive_file_id).
      payload.staged_file_id = form.staged_file_id;
    }
    await onSave(payload, form._isNew ? null : form.cabinet_line_id, form);
    setSaving(false);
  };

  return (
    <Dialog open={!!editLine} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="cab-edit-dialog">
        <DialogHeader>
          <DialogTitle>
            {form._isNew ? "Nouvelle dépense interne" : "Modifier la dépense"}
            {form._queueTotal > 1 && (
              <span
                className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-wider"
                data-testid="cab-edit-queue-badge"
              >
                Facture {form._queueIndex || 1} / {form._queueTotal}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>Toutes les valeurs en MAD sont calculées au taux du jour.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {previewUrl && previewUrl.startsWith("http") && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-2">
              <img src={previewUrl} alt="Aperçu" className="w-full max-h-72 object-contain rounded"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date *"><input type="date" max={maxDate} value={form.invoice_date || ""} onChange={(e) => set("invoice_date", e.target.value)} data-testid="cab-edit-date" /></Field>
            <Field label="N° Facture"><input type="text" value={form.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} data-testid="cab-edit-invoice" /></Field>
          </div>
          <Field label="Fournisseur *"><input type="text" value={form.vendor || ""} onChange={(e) => set("vendor", e.target.value)} data-testid="cab-edit-vendor" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Catégorie">
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="cab-edit-category">
                {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </Field>
            <Field label="Montant *"><input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="cab-edit-amount" /></Field>
            <Field label="Devise *">
              <select value={form.currency || "MAD"} onChange={(e) => set("currency", e.target.value)} data-testid="cab-edit-currency">
                {["MAD", "EUR", "USD", "XOF", "GBP", "AED", "TND", "CAD", "CHF"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="cab-edit-description" /></Field>
          {/* Iter-228x-bis — VAT breakdown panel restricted to MAD invoices
              only. Foreign VAT (EUR/USD/…) isn't recoverable for the cabinet's
              Moroccan VAT declaration, so we don't track it. */}
          {(form.currency || "MAD").toUpperCase() === "MAD" && (
          <div
            className="border border-zinc-200 rounded-md p-3 space-y-2 bg-zinc-50/50"
            data-testid="cab-edit-vat-block"
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              Détail TVA (MAD) — extrait automatiquement par l'OCR
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Montant HT">
                <input type="number" step="0.01" min="0"
                  value={form.amount_ht ?? ""}
                  onChange={(e) => {
                    const ht = e.target.value;
                    set("amount_ht", ht);
                    // Auto-calc TTC = HT + TVA when both numeric.
                    const tva = parseFloat(form.vat_amount);
                    const htn = parseFloat(ht);
                    if (Number.isFinite(htn) && Number.isFinite(tva)) {
                      const ttc = Math.round((htn + tva) * 100) / 100;
                      set("amount_ttc", String(ttc));
                      set("amount", String(ttc));
                    }
                  }}
                  data-testid="cab-edit-amount-ht" />
              </Field>
              <Field label="TVA (%)">
                <input type="number" step="0.1" min="0" max="100"
                  value={form.vat_rate ?? ""}
                  onChange={(e) => set("vat_rate", e.target.value)}
                  data-testid="cab-edit-vat-rate" />
              </Field>
              <Field label="Montant TVA">
                <input type="number" step="0.01" min="0"
                  value={form.vat_amount ?? ""}
                  onChange={(e) => {
                    const tva = e.target.value;
                    set("vat_amount", tva);
                    const ht = parseFloat(form.amount_ht);
                    const tvan = parseFloat(tva);
                    if (Number.isFinite(ht) && Number.isFinite(tvan)) {
                      const ttc = Math.round((ht + tvan) * 100) / 100;
                      set("amount_ttc", String(ttc));
                      set("amount", String(ttc));
                    }
                  }}
                  data-testid="cab-edit-vat-amount" />
              </Field>
              <Field label="Montant TTC">
                <input type="number" step="0.01" min="0"
                  value={form.amount_ttc ?? ""}
                  onChange={(e) => {
                    const ttc = e.target.value;
                    set("amount_ttc", ttc);
                    // Keep `amount` (grand total) in sync with TTC.
                    set("amount", ttc);
                  }}
                  data-testid="cab-edit-amount-ttc" />
              </Field>
            </div>
            <p className="text-[10px] text-zinc-500 leading-relaxed">
              Laissez vide si la facture ne comporte pas de TVA explicite. TTC est calculé automatiquement (HT + TVA).
            </p>
          </div>
          )}
          {/* Iter-207j — file attachment block. Allowed in both create AND edit mode.
              In edit mode, attaching a new file uploads it to Drive synchronously
              (same helper as creation) and replaces any pre-existing drive_file_id. */}
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-600 block">
              Justificatif {form._isNew ? "" : "(remplacer)"}
            </Label>
            {form.staged_file_id && !form._uploadingNew ? (
              <div className="flex items-center justify-between text-xs px-3 py-2 border border-emerald-200 bg-emerald-50 rounded">
                <span className="font-mono text-emerald-700">
                  ✓ Fichier en attente d'upload Drive · stg_{form.staged_file_id.slice(0, 8)}…
                </span>
                <button
                  type="button"
                  onClick={() => set("staged_file_id", null)}
                  data-testid="cab-edit-detach"
                  className="text-zinc-500 hover:text-zinc-900 text-[11px]"
                >
                  Retirer
                </button>
              </div>
            ) : !form._isNew && form.receipt_drive_web_view_link ? (
              <div className="flex items-center justify-between text-xs px-3 py-2 border border-zinc-200 bg-zinc-50 rounded">
                <a href={form.receipt_drive_web_view_link} target="_blank" rel="noreferrer" className="font-mono text-blue-600 hover:underline">
                  📎 Justificatif existant (cliquer pour voir)
                </a>
                <span className="text-zinc-400 text-[11px]">Uploadez un nouveau fichier pour remplacer ⤵</span>
              </div>
            ) : null}
            <input
              type="file"
              accept="image/*,application/pdf"
              data-testid="cab-edit-file-input"
              disabled={form._uploadingNew}
              className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:border-0 file:rounded file:bg-zinc-100 hover:file:bg-zinc-200 file:text-zinc-700 file:cursor-pointer"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                set("_uploadingNew", true);
                try {
                  const fd = new FormData();
                  fd.append("file", f);
                  const r = await api.post("/expenses/ocr", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  const sid = r.data?.staged_file?.staged_file_id;
                  const d = r.data?.draft || {};
                  if (sid) {
                    // Iter-228x-bis — auto-fill EMPTY fields from the OCR draft.
                    // Non-empty user inputs are preserved (the user already
                    // typed/corrected them — don't overwrite).
                    const isEmpty = (v) => v === undefined || v === null || v === "";
                    setForm((cur) => {
                      if (!cur) return cur;
                      const merged = { ...cur, staged_file_id: sid };
                      const tryFill = (key, ocrVal) => {
                        if (isEmpty(cur[key]) && ocrVal !== undefined && ocrVal !== null && ocrVal !== "") {
                          merged[key] = ocrVal;
                        }
                      };
                      tryFill("vendor", d.vendor);
                      tryFill("invoice_number", d.invoice_number);
                      tryFill("category", d.category);
                      tryFill("amount", d.amount);
                      tryFill("currency", d.currency);
                      tryFill("invoice_date", d.invoice_date);
                      tryFill("description", d.description);
                      // VAT block (MAD only — gated server-side at save anyway).
                      tryFill("amount_ht", d.amount_ht);
                      tryFill("vat_amount", d.vat_amount);
                      tryFill("vat_rate", d.vat_rate);
                      tryFill("amount_ttc", d.amount_ttc ?? d.amount);
                      return merged;
                    });
                    // Detect what was actually filled so the toast is helpful.
                    const filled = [];
                    if (isEmpty(form.amount_ht) && d.amount_ht != null) filled.push("HT");
                    if (isEmpty(form.vat_amount) && d.vat_amount != null) filled.push("TVA");
                    if (isEmpty(form.amount_ttc) && (d.amount_ttc != null || d.amount != null)) filled.push("TTC");
                    if (isEmpty(form.vendor) && d.vendor) filled.push("fournisseur");
                    if (isEmpty(form.amount) && d.amount != null) filled.push("montant");
                    toast.success(
                      filled.length
                        ? `Fichier prêt — champs auto-remplis : ${filled.join(", ")}.`
                        : "Fichier prêt — cliquez Enregistrer pour upload sur Drive."
                    );
                  } else {
                    toast.error("Échec du staging du fichier.");
                  }
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Échec de l'upload");
                } finally {
                  set("_uploadingNew", false);
                  e.target.value = "";
                }
              }}
            />
            {form._uploadingNew && (
              <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Analyse en cours…
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="inline-flex items-center gap-1 px-3 h-9 border border-zinc-200 rounded text-zinc-700 hover:bg-zinc-50 text-sm"><X size={12} /> {form._isNew && (form._queueTotal || 1) > 1 ? "Annuler tout" : "Annuler"}</button>
          {form._isNew && (form._queueTotal || 1) > 1 && (
            <button
              onClick={() => onSkip?.(form)}
              data-testid="cab-edit-skip"
              title="Ignorer cette facture (ex. dépense déjà existante) et passer au fichier scanné suivant"
              className="inline-flex items-center gap-1 px-3 h-9 border border-amber-300 bg-amber-50 rounded text-amber-800 hover:bg-amber-100 text-sm"
            >
              Ignorer → suivante
            </button>
          )}
          <button onClick={submit} disabled={saving} data-testid="cab-edit-save"
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-zinc-950 text-white rounded text-sm hover:bg-zinc-800 disabled:opacity-50">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  const onlyChild = React.Children.only(children);
  return (
    <label className="block">
      <Label className="text-xs text-zinc-600 mb-1 block">{label}</Label>
      {React.cloneElement(onlyChild, {
        className: "w-full px-2.5 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950",
      })}
    </label>
  );
}
